import React, { useState, useEffect, useMemo } from "react";
import { toast } from "react-toastify";
import { Autocomplete, TextField } from "@mui/material";
import { useGrantCredits, useSplitGrantCredits, useUsers, useAllocations, useUpdateAllocation, useTenantById, useCreditUsage, useTenantUserById } from "@/hooks/useTenantDashboardService";
import { useActiveTenants } from "@/hooks/useAdminDashboardService";
import useAuthStore from "@/store/useAuthStore";

function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);
    return () => clearTimeout(handler);
  }, [value, delay]);
  return debouncedValue;
}

const getRoleBadge = (role?: string, user?: any) => {
  const normalizedRole = role?.toUpperCase();
  const tenantType = user?.tenant?.type || user?.tenantType;
  const isCompanyUser = tenantType === "COMPANY";

  let bgClass = "bg-cyan-500/10 text-cyan-400 border-cyan-500/20";
  let label = isCompanyUser ? "Company User" : "";

  if (normalizedRole === "ADMIN" || normalizedRole === "SUPER_ADMIN" || normalizedRole === "COMPANY_ADMIN") {
    bgClass = "bg-rose-500/10 text-rose-400 border-rose-500/20";
    label = isCompanyUser ? "Company Admin" : "";
  } else if (normalizedRole === "MANAGER") {
    bgClass = "bg-indigo-500/10 text-indigo-400 border-indigo-500/20";
    label = isCompanyUser ? "Company Manager" : "";
  } else if (normalizedRole === "REP") {
    bgClass = "bg-emerald-500/10 text-emerald-400 border-emerald-500/20";
    label = isCompanyUser ? "Company Rep" : "";
  } else {
    bgClass = "bg-cyan-500/10 text-cyan-400 border-cyan-500/20";
    label = isCompanyUser ? "Company User" : "";
  }

  if (!label || label === "Individual User") return null;

  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-semibold border ${bgClass}`}>
      {label}
    </span>
  );
};

const GrantCreditsPage: React.FC = () => {
  const { role, tenantid, id: loggedInUserId, companyName, userName } = useAuthStore();

  const { data: tenantData } = useTenantById({
    tenantId: role === "ADMIN" ? tenantid : "",
    query: {},
  });

  const { data: loggedInUserData } = useTenantUserById(
    role === "ADMIN" ? loggedInUserId : "",
    role === "ADMIN" && !!loggedInUserId
  );

  const { data: creditUsageData, isLoading: isLoadingCredits } = useCreditUsage(
    {
      tenantId: role === "ADMIN" ? tenantid : undefined,
    },
    role === "ADMIN" && !!tenantid
  );

  const { balances = [] } = (creditUsageData as any) || {};
  const tenantBalanceObj = balances.find((b: any) => b.userId === null);
  const tenantBalance = tenantBalanceObj ? tenantBalanceObj.balance : 0;
  const adminName = loggedInUserData?.name || userName || "";
  const adminEmail = loggedInUserData?.email || tenantData?.contactEmail || "";

  // Single vs Split Grant State
  const [grantType, setGrantType] = useState<"SINGLE" | "SPLIT">("SINGLE");
  const [splitMode, setSplitMode] = useState<"FULL_POOL" | "PER_USER">("FULL_POOL");
  const [perUserAmount, setPerUserAmount] = useState<number | "">(30);

  const [targetType, setTargetType] = useState<"USER" | "TENANT">("TENANT");
  const [searchTerm, setSearchTerm] = useState("");
  const debouncedSearch = useDebounce(searchTerm, 500);
  const [selectedTarget, setSelectedTarget] = useState<any | null>(null);
  const selectedTargetId = selectedTarget?.id || "";
  const [amount, setAmount] = useState<number>(0);
  const [expiresAt, setExpiresAt] = useState<string>("");

  // Edit allocation state
  const [editingAllocation, setEditingAllocation] = useState<{ id: string; name: string; currentCredits: number } | null>(null);
  const [editAmount, setEditAmount] = useState<number>(0);

  const grantCreditsMutation = useGrantCredits();
  const splitGrantMutation = useSplitGrantCredits();
  const { data: allocationsData, isLoading: isAllocationsLoading } = useAllocations();
  const updateAllocationMutation = useUpdateAllocation();

  useEffect(() => {
    if (role === "ADMIN") {
      setTargetType("USER");
    } else {
      setTargetType("TENANT");
      setGrantType("SINGLE");
    }
  }, [role]);

  const { data: usersData, isLoading: isUsersLoading } = useUsers({
    currentPage: 1,
    pageSize: 100, // Fetch list for target and split calculations
    search: targetType === "USER" || grantType === "SPLIT" ? debouncedSearch : "",
    currentAuthRole: role,
    role: "", // fetch all
  });

  const { data: tenantsData, isLoading: isTenantsLoading } = useActiveTenants(
    targetType === "TENANT" ? { searchKeyword: debouncedSearch } : undefined
  );

  const rawUsersList = useMemo(() => {
    const list = usersData?.data?.items || usersData?.data?.users || usersData?.data;
    return Array.isArray(list) ? list : [];
  }, [usersData]);

  const usersList = useMemo(() => {
    if (!Array.isArray(rawUsersList)) return [];

    // For Company Admin, show all Users & Managers in their company
    if (role === "ADMIN") {
      return rawUsersList.filter((u: any) => {
        const r = (u?.role || "").toUpperCase();
        return r !== "SUPER_ADMIN" && r !== "SUPER_ADMIN_ROLE";
      });
    }

    // For Super Admin selecting Individual User target type
    if (targetType === "USER") {
      // 1. Identify Company Tenants (tenants where type is COMPANY or has a COMPANY_ADMIN / ADMIN)
      const companyTenantIds = new Set<string>();

      rawUsersList.forEach((u: any) => {
        const tId = u?.tenantId || u?.tenant?.id;
        const r = (u?.role || "").toUpperCase();
        const tType = (u?.tenant?.type || u?.tenantType || "").toUpperCase();

        if (tId && (tType === "COMPANY" || r === "ADMIN" || r === "COMPANY_ADMIN")) {
          companyTenantIds.add(tId);
        }
      });

      // 2. Filter ONLY true individual workspace users
      return rawUsersList.filter((u: any) => {
        const r = (u?.role || "").toUpperCase();
        const tId = u?.tenantId || u?.tenant?.id;
        const tType = (u?.tenant?.type || u?.tenantType || "").toUpperCase();

        // Exclude Admins, Super Admins, Company Admins & Managers
        if (r === "ADMIN" || r === "SUPER_ADMIN" || r === "COMPANY_ADMIN" || r === "MANAGER") return false;

        // Exclude COMPANY tenant type
        if (tType === "COMPANY") return false;

        // Exclude users belonging to any tenant marked as a company tenant
        if (tId && companyTenantIds.has(tId)) {
          return false;
        }

        return true;
      });
    }

    return rawUsersList;
  }, [rawUsersList, targetType, role]);

  const tenantsList = useMemo(() => {
    const list = tenantsData?.data?.items || tenantsData?.data;
    return Array.isArray(list) ? list : [];
  }, [tenantsData]);

  // Split Calculations: Combine company users, managers, and admins (excluding Super Admin)
  const eligibleUsers = useMemo(() => {
    const userMap = new Map<string, any>();

    const isEligibleRecipient = (roleStr?: string) => {
      const r = roleStr?.toUpperCase() || "";
      // Exclude Super Admins only
      if (r === "SUPER_ADMIN" || r === "SUPER_ADMIN_ROLE") {
        return false;
      }
      return true;
    };

    // 1. Add team members from rawUsersList (USER, MANAGER, ADMIN)
    if (Array.isArray(rawUsersList)) {
      rawUsersList.forEach((u: any) => {
        const uId = u.id || u._id;
        if (uId && isEligibleRecipient(u.role)) {
          userMap.set(uId, u);
        }
      });
    }

    // 2. Add team members from allocationsData (if any exist not in rawUsersList)
    if (allocationsData?.allocations && Array.isArray(allocationsData.allocations)) {
      allocationsData.allocations.forEach((a: any) => {
        if (a.user) {
          const uId = a.user.id || a.user._id;
          if (uId && isEligibleRecipient(a.user.role) && !userMap.has(uId)) {
            userMap.set(uId, a.user);
          }
        }
      });
    }

    return Array.from(userMap.values());
  }, [rawUsersList, allocationsData]);

  const roleBreakdown = useMemo(() => {
    let admins = 0;
    let managers = 0;
    let users = 0;
    eligibleUsers.forEach((u: any) => {
      const r = (u.role || "").toUpperCase();
      if (r === "ADMIN" || r === "COMPANY_ADMIN") {
        admins++;
      } else if (r === "MANAGER") {
        managers++;
      } else {
        users++;
      }
    });
    return { admins, managers, users };
  }, [eligibleUsers]);

  const recipientBreakdownText = useMemo(() => {
    const parts: string[] = [];
    if (roleBreakdown.managers > 0) {
      parts.push(`${roleBreakdown.managers} ${roleBreakdown.managers === 1 ? "Manager" : "Managers"}`);
    }
    if (roleBreakdown.users > 0) {
      parts.push(`${roleBreakdown.users} ${roleBreakdown.users === 1 ? "User" : "Users"}`);
    }
    if (roleBreakdown.admins > 0) {
      parts.push(`${roleBreakdown.admins} ${roleBreakdown.admins === 1 ? "Admin" : "Admins"}`);
    }
    return parts.length > 0 ? parts.join(", ") : "team members";
  }, [roleBreakdown]);

  const recipientCount = eligibleUsers.length;
  const availablePoolCredits = tenantBalance;

  const calculatedPerUser =
    splitMode === "FULL_POOL"
      ? recipientCount > 0
        ? Math.floor(availablePoolCredits / recipientCount)
        : 0
      : Number(perUserAmount) || 0;

  const totalPoolConsumed = calculatedPerUser * recipientCount;
  const remainingPool = availablePoolCredits - totalPoolConsumed;

  // Derive Organization Pool Expiration Date (matching table expiration)
  const orgExpiryDate = useMemo(() => {
    if (allocationsData?.allocations && Array.isArray(allocationsData.allocations) && allocationsData.allocations.length > 0) {
      const orgAlloc = allocationsData.allocations.find((a: any) => (a.tenant || !a.user) && a.expiresAt);
      if (orgAlloc?.expiresAt) {
        return orgAlloc.expiresAt;
      }
      const anyAlloc = allocationsData.allocations.find((a: any) => a.expiresAt);
      if (anyAlloc?.expiresAt) {
        return anyAlloc.expiresAt;
      }
      const firstAlloc = allocationsData.allocations[0];
      if (firstAlloc?.createdAt) {
        const d = new Date(firstAlloc.createdAt);
        d.setMonth(d.getMonth() + 1);
        return d.toISOString();
      }
    }
    // Default fallback to 1 month from today
    const defaultExpiry = new Date();
    defaultExpiry.setMonth(defaultExpiry.getMonth() + 1);
    return defaultExpiry.toISOString();
  }, [allocationsData]);
  useEffect(() => {
    setSelectedTarget(null);
    setSearchTerm("");
  }, [targetType]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedTargetId) {
      toast.error("Please select a user or organization");
      return;
    }
    if (amount <= 0) {
      toast.error("Amount must be greater than 0");
      return;
    }

    grantCreditsMutation.mutate(
      {
        targetType,
        targetId: selectedTargetId,
        amount,
        expiresAt: expiresAt || undefined,
      },
      {
        onSuccess: () => {
          toast.success("Credits granted successfully");
          setAmount(0);
          setSearchTerm("");
          setSelectedTarget(null);
          setExpiresAt("");
        },
        onError: (err: any) => {
          toast.error(err.response?.data?.message || "Failed to grant credits");
        },
      }
    );
  };

  const handleSplitSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (recipientCount === 0) {
      toast.error("No eligible users or managers found to split credits.");
      return;
    }

    if (splitMode === "PER_USER" && (!perUserAmount || Number(perUserAmount) <= 0)) {
      toast.error("Please enter a valid credit amount per user.");
      return;
    }

    splitGrantMutation.mutate(
      {
        splitMode,
        amount: splitMode === "PER_USER" ? Number(perUserAmount) : undefined,
      },
      {
        onSuccess: (res: any) => {
          toast.success(res?.message || "Credits split successfully among team!");
        },
        onError: (err: any) => {
          toast.error(err.response?.data?.message || "Failed to split credits.");
        },
      }
    );
  };

  const showTableCard = isAllocationsLoading || (allocationsData?.allocations && allocationsData.allocations.length > 0);

  return (
    <div className={`flex-1 p-2 md:p-4 mt-6 transition-all duration-300 w-full ${showTableCard ? "max-w-none" : "max-w-2xl mx-auto"}`}>
      <div className={showTableCard ? "grid grid-cols-1 lg:grid-cols-12 gap-4 items-start" : "space-y-4"}>
        {/* Form Card */}
        <div className={`rounded-lg p-4 md:p-6 shadow-lg border border-[var(--color-border)] bg-[var(--color-surface)] ${showTableCard ? "lg:col-span-5" : ""}`}>
          <div className="flex flex-col md:flex-row md:items-start justify-between gap-4 mb-6 pb-4 border-b border-[var(--color-border)]">
            <h2 className="text-2xl font-bold text-[var(--color-text)]">Grant Credits</h2>
            {role === "ADMIN" && (
              <div className="flex flex-col gap-2 text-xs md:items-end">
                <div className="px-3 py-1.5 rounded-full border border-[var(--color-border)] bg-[var(--color-bg)] text-[var(--color-text)] flex items-center gap-1.5 shadow-sm w-fit">
                  <span className="text-[var(--color-muted)] font-medium">Company:</span>
                  <span className="font-semibold text-indigo-400">{companyName || tenantData?.name || "—"}</span>
                </div>
                <div className="px-3 py-1.5 rounded-full border border-[var(--color-border)] bg-[var(--color-bg)] text-[var(--color-text)] flex items-center gap-1.5 shadow-sm w-fit">
                  <span className="text-[var(--color-muted)] font-medium">Admin:</span>
                  <span className="font-semibold text-indigo-400">
                    {adminName ? `${adminName} (${adminEmail})` : adminEmail}
                  </span>
                </div>
                <div className="px-3 py-1.5 rounded-full border border-[var(--color-border)] bg-[var(--color-bg)] text-[var(--color-text)] flex items-center gap-1.5 shadow-sm w-fit">
                  <span className="text-[var(--color-muted)] font-medium">Available Credits:</span>
                  <span className="font-semibold text-indigo-400">{isLoadingCredits ? "..." : tenantBalance}</span>
                </div>
              </div>
            )}
          </div>

          {/* Grant Type Selector (Single vs Split) - Only for Company Admin */}
          {role !== "SUPER_ADMIN" && (
            <div className="flex gap-3 mb-6 bg-[var(--color-bg)] p-1.5 rounded-lg border border-[var(--color-border)]">
              <button
                type="button"
                onClick={() => setGrantType("SINGLE")}
                className={`flex-1 py-2 text-sm font-medium rounded-md transition-all ${grantType === "SINGLE"
                  ? "bg-indigo-600 text-white shadow font-bold"
                  : "text-[var(--color-muted)] hover:text-[var(--color-text)]"
                  }`}
              >
                Single User Grant
              </button>
              <button
                type="button"
                onClick={() => setGrantType("SPLIT")}
                className={`flex-1 py-2 text-sm font-medium rounded-md transition-all flex items-center justify-center gap-2 ${grantType === "SPLIT"
                  ? "bg-indigo-600 text-white shadow font-bold"
                  : "text-[var(--color-muted)] hover:text-[var(--color-text)]"
                  }`}
              >
                <span>⚡ Split Pool Across Team</span>
                <span className="bg-emerald-500/20 text-emerald-400 text-[10px] px-2 py-0.5 rounded-full font-bold">New</span>
              </button>
            </div>
          )}

          {grantType === "SINGLE" ? (
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Target Type */}
              {role === "SUPER_ADMIN" && (
                <div>
                  <label className="block text-sm font-medium text-[var(--color-text)] mb-2">Target Type</label>
                  <div className="flex gap-4">
                    <label className="flex items-center text-[var(--color-text)] cursor-pointer">
                      <input
                        type="radio"
                        name="targetType"
                        value="TENANT"
                        checked={targetType === "TENANT"}
                        onChange={() => setTargetType("TENANT")}
                        className="mr-2"
                      />
                      Organization
                    </label>
                    <label className="flex items-center text-[var(--color-text)] cursor-pointer">
                      <input
                        type="radio"
                        name="targetType"
                        value="USER"
                        checked={targetType === "USER"}
                        onChange={() => setTargetType("USER")}
                        className="mr-2"
                      />
                      Individual User
                    </label>
                  </div>
                </div>
              )}

              {/* Target Selection with Autocomplete */}
              <div>
                <label className="block text-sm font-medium text-[var(--color-text)] mb-2">
                  Select {targetType === "TENANT" ? "Organization" : "User"}
                </label>
                <Autocomplete
                  options={targetType === "TENANT" ? tenantsList : usersList}
                  getOptionLabel={(option: any) => {
                    if (targetType === "TENANT") {
                      return option.name + (option.domain ? ` (${option.domain})` : "");
                    }
                    return (option.name || option.first_name + " " + option.last_name) + ` (${option.email})`;
                  }}
                  isOptionEqualToValue={(option: any, val: any) => option?.id === val?.id}
                  loading={isUsersLoading || isTenantsLoading}
                  value={selectedTarget}
                  onChange={(_, newValue: any) => {
                    setSelectedTarget(newValue);
                  }}
                  onInputChange={(_, newInputValue, reason) => {
                    if (reason === "input") {
                      setSearchTerm(newInputValue);
                    } else if (reason === "clear") {
                      setSearchTerm("");
                    }
                  }}
                  renderOption={(props, option: any) => {
                    if (targetType === "TENANT") {
                      return (
                        <li {...props} key={option.id}>
                          <div className="flex items-center justify-between w-full">
                            <span>{option.name} {option.domain ? `(${option.domain})` : ""}</span>
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-semibold border bg-emerald-500/10 text-emerald-400 border-emerald-500/20 ml-2">
                              Organization
                            </span>
                          </div>
                        </li>
                      );
                    }
                    return (
                      <li {...props} key={option.id}>
                        <div className="flex items-center justify-between w-full">
                          <span>{option.name || `${option.first_name || ""} ${option.last_name || ""}`.trim() || "User"} ({option.email})</span>
                          <div className="ml-2">
                            {getRoleBadge(option.role, option)}
                          </div>
                        </div>
                      </li>
                    );
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder={`Search ${targetType === "TENANT" ? "organization" : "user"} by name or email...`}
                      variant="outlined"
                      size="small"
                      sx={{
                        "& .MuiInputBase-root": {
                          color: "var(--color-text)",
                          backgroundColor: "var(--color-bg)",
                          borderRadius: "0.375rem",
                        },
                        "& .MuiOutlinedInput-notchedOutline": {
                          borderColor: "var(--color-border)",
                        },
                        "& .MuiInputBase-root:hover .MuiOutlinedInput-notchedOutline": {
                          borderColor: "var(--color-border)",
                        },
                      }}
                    />
                  )}
                />
              </div>

              {/* Amount */}
              <div>
                <label className="block text-sm font-medium text-[var(--color-text)] mb-2">Amount</label>
                <input
                  type="number"
                  min="1"
                  value={amount || ""}
                  onChange={(e) => setAmount(Number(e.target.value))}
                  placeholder="e.g. 100"
                  className="w-full bg-[var(--color-bg)] text-[var(--color-text)] rounded-md px-4 py-2 border border-[var(--color-border)] focus:outline-none focus:border-indigo-500"
                  required
                />
                {role === "ADMIN" && orgExpiryDate && (
                  <p className="text-[11px] text-[var(--color-muted)] text-right mt-1 font-medium">
                    Organisation Pool Expiry: <span className="text-amber-400 font-semibold">{new Date(orgExpiryDate).toLocaleDateString()}</span>
                  </p>
                )}
              </div>

              {/* Expiration Date - Super Admin Only */}
              {role === "SUPER_ADMIN" && (
                <div>
                  <label className="block text-sm font-medium text-[var(--color-text)] mb-2">
                    Expiration Date (Optional)
                  </label>
                  <input
                    type="date"
                    value={expiresAt}
                    onChange={(e) => setExpiresAt(e.target.value)}
                    min={new Date().toISOString().split("T")[0]}
                    className="w-full bg-[var(--color-bg)] text-[var(--color-text)] rounded-md px-4 py-2 border border-[var(--color-border)] focus:outline-none focus:border-indigo-500 [color-scheme:dark_light]"
                  />
                  <p className="text-xs text-[var(--color-muted)] mt-1">If left blank, it will default to 1 year from today.</p>
                </div>
              )}

              {/* Submit Button */}
              <div className="pt-4">
                <button
                  type="submit"
                  disabled={grantCreditsMutation.isPending || !selectedTargetId || amount <= 0}
                  className="w-full bg-indigo-600 text-white font-bold py-3 px-4 rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {grantCreditsMutation.isPending ? "Granting..." : "Grant Credits"}
                </button>
              </div>
            </form>
          ) : (
            /* FORM 2: Split Organisation Credit Form */
            <form onSubmit={handleSplitSubmit} className="space-y-5">
              <div className="bg-indigo-950/40 border border-indigo-500/20 rounded-lg p-3.5 text-xs text-indigo-200">
                Split will distribute credits among all <strong>{recipientCount} active team members ({recipientBreakdownText})</strong> in your organisation.
              </div>

              {/* Split Mode Options */}
              <div className="space-y-3">
                <label className="block text-sm font-medium text-[var(--color-text)]">Choose Split Option</label>

                {/* Option A: Full Pool Split */}
                <label
                  className={`flex items-start p-3.5 rounded-lg border cursor-pointer transition ${splitMode === "FULL_POOL"
                    ? "bg-indigo-600/10 border-indigo-500"
                    : "bg-[var(--color-bg)] border-[var(--color-border)] hover:border-indigo-500/50"
                    }`}
                >
                  <input
                    type="radio"
                    name="splitMode"
                    value="FULL_POOL"
                    checked={splitMode === "FULL_POOL"}
                    onChange={() => setSplitMode("FULL_POOL")}
                    className="mt-1 text-indigo-600 focus:ring-indigo-500"
                  />
                  <div className="ml-3">
                    <span className="block text-sm font-semibold text-[var(--color-text)]">Full Pool Equal Split </span>
                    <span className="block text-xs text-[var(--color-muted)] mt-0.5">
                      Divides the entire {availablePoolCredits} available credits equally among {recipientCount} team members ({recipientBreakdownText}).
                    </span>
                  </div>
                </label>

                {/* Option B: Custom Per User Input Amount */}
                <label
                  className={`flex items-start p-3.5 rounded-lg border cursor-pointer transition ${splitMode === "PER_USER"
                    ? "bg-indigo-600/10 border-indigo-500"
                    : "bg-[var(--color-bg)] border-[var(--color-border)] hover:border-indigo-500/50"
                    }`}
                >
                  <input
                    type="radio"
                    name="splitMode"
                    value="PER_USER"
                    checked={splitMode === "PER_USER"}
                    onChange={() => setSplitMode("PER_USER")}
                    className="mt-1 text-indigo-600 focus:ring-indigo-500"
                  />
                  <div className="ml-3 w-full">
                    <span className="block text-sm font-semibold text-[var(--color-text)]">Custom Credit Amount Per Person</span>
                    <span className="block text-xs text-[var(--color-muted)] mt-0.5 mb-2">
                      Enter specific amount per person ({recipientBreakdownText}). Remaining credits stay in organisation pool.
                    </span>

                    {splitMode === "PER_USER" && (
                      <div className="mt-2" onClick={(e) => e.stopPropagation()}>
                        <input
                          type="number"
                          min="1"
                          value={perUserAmount}
                          onChange={(e) => setPerUserAmount(e.target.value === "" ? "" : Number(e.target.value))}
                          placeholder="e.g. 30"
                          className="w-full max-w-xs bg-[var(--color-bg)] border border-[var(--color-border)] rounded-md px-3 py-2 text-[var(--color-text)] focus:outline-none focus:border-indigo-500 text-sm"
                          required
                        />
                      </div>
                    )}
                  </div>
                </label>
              </div>

              {/* Live Calculation Preview Box */}
              <div className="bg-[var(--color-bg)] border border-[var(--color-border)] rounded-lg p-4 text-xs space-y-2">
                <div className="flex justify-between items-center">
                  <span className="text-[var(--color-muted)]">Target Recipients:</span>
                  <span className="font-semibold text-[var(--color-text)] text-right">
                    {recipientCount} ({recipientBreakdownText})
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-[var(--color-muted)]">Credits Granted Per Person:</span>
                  <span className="font-semibold text-emerald-400">{calculatedPerUser} Credits</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-[var(--color-muted)]">Total Credits Consumed:</span>
                  <span className="font-semibold text-amber-400">{totalPoolConsumed} Credits</span>
                </div>
                <div className="flex justify-between pt-2 border-t border-[var(--color-border)]">
                  <span className="text-[var(--color-muted)]">Remaining Organisation Pool:</span>
                  <span className={`font-bold ${remainingPool < 0 ? "text-rose-400" : "text-emerald-400"}`}>
                    {remainingPool} Credits
                  </span>
                </div>
                {role === "ADMIN" && orgExpiryDate && (
                  <div className="flex justify-between pt-2 border-t border-[var(--color-border)] text-[11px]">
                    <span className="text-[var(--color-muted)]">Organisation Pool Expiry:</span>
                    <span className="font-semibold text-amber-400">
                      {new Date(orgExpiryDate).toLocaleDateString()}
                    </span>
                  </div>
                )}
              </div>

              <div className="pt-2">
                <button
                  type="submit"
                  disabled={splitGrantMutation.isPending || (role === "ADMIN" && remainingPool < 0) || recipientCount === 0}
                  className="w-full py-3 px-4 bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 text-white font-semibold rounded-lg shadow transition"
                >
                  {splitGrantMutation.isPending ? "Splitting Credits..." : `Execute Split (${totalPoolConsumed} Credits Total)`}
                </button>
              </div>
            </form>
          )}
        </div>

        {/* Credit Allocations Table */}
        {showTableCard && (
          <div className="rounded-lg p-4 md:p-6 shadow-lg border border-[var(--color-border)] bg-[var(--color-surface)] lg:col-span-7">
            <h3 className="text-xl font-bold text-[var(--color-text)] mb-4">Credit Allocations</h3>
            {isAllocationsLoading ? (
              <p className="text-[var(--color-muted)] text-sm">Loading allocations...</p>
            ) : !allocationsData?.allocations || allocationsData.allocations.length === 0 ? (
              <p className="text-[var(--color-muted)] text-sm">No credit allocations found.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-left text-sm border-collapse">
                  <thead>
                    <tr className="border-b border-[var(--color-border)] text-[var(--color-text)] opacity-80">
                      <th className="py-3 px-4 font-semibold">User / Organization</th>
                      <th className="py-3 px-4 font-semibold text-center">Allocated</th>
                      <th className="py-3 px-4 text-center font-semibold">Consumed</th>
                      <th className="py-3 px-4 text-center font-semibold">Remaining</th>
                      <th className="py-3 px-4 font-semibold">Expiration</th>
                      <th className="py-3 px-4 font-semibold text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[var(--color-border)] text-[var(--color-text)]">
                    {allocationsData.allocations.map((alloc: any) => {
                      const isExpired = alloc.isExpired ?? (alloc.expiresAt ? new Date(alloc.expiresAt) <= new Date() : false);
                      const displayRemaining = isExpired ? 0 : (alloc.remaining ?? Math.max(0, alloc.credits - (alloc.consumed || 0)));

                      return (
                        <tr key={alloc.id} className="hover:bg-[var(--color-bg)] transition-colors">
                          <td className="py-3 px-4">
                            {alloc.tenant ? (
                              <>
                                <div className="flex items-center gap-2">
                                  <span className="font-semibold text-base">{alloc.tenant.name}</span>
                                  <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-semibold border bg-emerald-500/10 text-emerald-400 border-emerald-500/20">
                                    Organization
                                  </span>
                                </div>
                                <div className="text-xs text-[var(--color-muted)]">{alloc.tenant.domain || "No Domain"}</div>
                              </>
                            ) : (
                              <>
                                <div className="flex items-center gap-2">
                                  <span className="font-semibold text-base">{alloc.user?.name || "Unknown User"}</span>
                                  {alloc.user && getRoleBadge(alloc.user.role, alloc.user)}
                                </div>
                                <div className="text-xs text-[var(--color-muted)]">{alloc.user?.email}</div>
                              </>
                            )}
                          </td>
                          <td className="py-3 px-4 text-center font-semibold text-indigo-500 text-base">{alloc.credits}</td>
                          <td className="py-3 px-4 text-center text-[var(--color-muted)] text-base">{alloc.consumed || 0}</td>
                          <td className="py-3 px-4 text-center font-bold text-base">
                            <span className={isExpired ? "text-rose-400 opacity-75" : "text-emerald-500"}>
                              {displayRemaining}
                            </span>
                          </td>
                          <td className="py-3 px-4 text-xs text-[var(--color-muted)]">
                            {alloc.expiresAt ? (
                              <div className="flex flex-col">
                                <span>{new Date(alloc.expiresAt).toLocaleDateString()}</span>
                                {isExpired && (
                                  <span className="text-[10px] text-rose-400 font-semibold">(Expired)</span>
                                )}
                              </div>
                            ) : (
                              "Never"
                            )}
                          </td>
                          <td className="py-3 px-4 text-right">
                            <button
                              onClick={() => {
                                setEditingAllocation({
                                  id: alloc.id,
                                  name: alloc.tenant ? alloc.tenant.name : (alloc.user?.name || alloc.user?.email || "User"),
                                  currentCredits: alloc.credits,
                                });
                                setEditAmount(alloc.credits);
                              }}
                              className="bg-transparent hover:bg-indigo-500/10 text-indigo-600 font-semibold hover:text-indigo-700 py-1.5 px-4 border border-indigo-500 hover:border-transparent rounded text-sm transition-all duration-200"
                            >
                              Modify
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Edit Allocation Modal */}
      {editingAllocation && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
          <div className="bg-[var(--color-surface)] border border-[var(--color-border)] rounded-lg p-6 max-w-md w-full shadow-2xl animate-fade-in">
            <h3 className="text-lg font-bold text-[var(--color-text)] mb-2">Modify Credit Allocation</h3>
            <p className="text-sm text-[var(--color-muted)] mb-4">
              Update credits for <span className="font-semibold text-[var(--color-text)]">{editingAllocation.name}</span>.
            </p>

            <div className="space-y-4">
              <div>
                <label className="block text-xs font-medium text-[var(--color-muted)] mb-1">
                  Current Allocation
                </label>
                <div className="text-sm font-semibold text-[var(--color-text)] bg-[var(--color-bg)] rounded px-3 py-2 border border-[var(--color-border)]">
                  {editingAllocation.currentCredits} credits
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-[var(--color-text)] mb-2">
                  New Allocation Size
                </label>
                <input
                  type="number"
                  min="0"
                  value={editAmount}
                  onChange={(e) => setEditAmount(Number(e.target.value))}
                  placeholder="e.g. 150"
                  className="w-full bg-[var(--color-bg)] text-[var(--color-text)] rounded-md px-4 py-2 border border-[var(--color-border)] focus:outline-none focus:border-indigo-500"
                  required
                />
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <button
                type="button"
                onClick={() => setEditingAllocation(null)}
                className="bg-transparent hover:bg-[var(--color-bg)] text-[var(--color-text)] font-semibold py-2 px-4 border border-[var(--color-border)] rounded-md text-sm transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={updateAllocationMutation.isPending || editAmount < 0}
                onClick={() => {
                  updateAllocationMutation.mutate(
                    {
                      grantId: editingAllocation.id,
                      amount: editAmount,
                    },
                    {
                      onSuccess: () => {
                        toast.success("Allocation updated successfully");
                        setEditingAllocation(null);
                      },
                      onError: (err: any) => {
                        toast.error(err.response?.data?.message || "Failed to update allocation");
                      },
                    }
                  );
                }}
                className="bg-indigo-600 hover:bg-indigo-700 text-white font-bold py-2 px-4 rounded-md text-sm disabled:opacity-50 transition-colors"
              >
                {updateAllocationMutation.isPending ? "Saving..." : "Save Changes"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GrantCreditsPage;
